import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { apiClient, setAuthFailureHandler } from './api-client';

export type AdminRole = 'admin' | 'viewer';

export interface AdminUser {
  id: string;
  email: string;
  role: AdminRole;
  must_change_password: boolean;
}

interface AuthContextValue {
  user: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AdminUser>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
  setUser: (u: AdminUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);

  async function refetch(): Promise<void> {
    try {
      const res = await apiClient.get<AdminUser>('/admin/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    }
  }

  useEffect(() => {
    // Register the auth-failure handler. When the api-client's refresh fails,
    // it calls this; we set user=null and React Router's ProtectedRoute handles
    // the redirect to /login (NO full page reload, NO loop).
    setAuthFailureHandler(() => {
      setUser(null);
    });

    // Bootstrap: try to fetch current user from /me (cookie-based).
    // /me is in the api-client's SKIP_REFRESH_PATHS, so a 401 here will NOT
    // trigger a refresh attempt — it just sets user=null.
    (async () => {
      try {
        const res = await apiClient.get<AdminUser>('/admin/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => setAuthFailureHandler(null);
  }, []);

  async function login(email: string, password: string): Promise<AdminUser> {
    const res = await apiClient.post<AdminUser>('/admin/auth/login', { email, password });
    setUser(res.data);
    return res.data;
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.post('/admin/auth/logout');
    } catch {
      // ignore
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetch,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
